import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Input, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import JlfcModal from './JlfcModal';
import MarkdownDocViewer from './MarkdownDocViewer';
import SearchFilterInput from './SearchFilterInput';
import { getAccentColor } from '../lib/jlfcTheme';
import { parseChangelogVersions, versionSearchQuery } from '../lib/changelogVersions';
import { countMarkdownSearchMatches } from '../lib/renderSimpleMarkdown';

/** Hauteur scrollable : modale − header, recherche, padding Paper, footer « Fermer », marges */
const SCROLL_HEIGHT = 'calc(100dvh - 21rem)';

export default function ReleaseNotesModal({ opened, onClose, appVersion }) {
  const [releaseNotes, setReleaseNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const versions = useMemo(() => parseChangelogVersions(releaseNotes), [releaseNotes]);

  useEffect(() => {
    if (!opened) {
      setSearchQuery('');
      return undefined;
    }

    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/CHANGELOG.md', { cache: 'no-cache' });
        if (!res.ok) throw new Error('missing');
        const text = await res.text();
        if (!cancelled) setReleaseNotes(text);
      } catch {
        if (!cancelled) {
          setReleaseNotes('# Release Notes\n\nAucune note de version disponible.');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [opened]);

  const activeVersion = useMemo(() => {
    const match = versions.find((version) => searchQuery === versionSearchQuery(version));
    return match || null;
  }, [searchQuery, versions]);

  const matchCount = useMemo(
    () => countMarkdownSearchMatches(releaseNotes, searchQuery),
    [releaseNotes, searchQuery],
  );

  return (
    <JlfcModal
      opened={opened}
      onClose={onClose}
      title={`Release Notes — v${appVersion}`}
      size="90%"
      showCloseFooter
    >
      <Stack gap="sm">
        <Grid gutter="md" align="flex-end">
          <Grid.Col span={{ base: 12, md: 2 }}>
            <Input.Label size="sm" mb={0}>
              Versions
            </Input.Label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 10 }}>
            <SearchFilterInput
              placeholder="Rechercher dans les release notes…"
              name="jlfc-release-notes-search"
              aria-label="Rechercher dans les release notes"
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />
          </Grid.Col>
        </Grid>

        {searchQuery.trim() ? (
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 10 }} offset={{ base: 0, md: 2 }}>
              <Text size="xs" c="dimmed">
                {matchCount > 0
                  ? `${matchCount} ligne${matchCount > 1 ? 's' : ''} correspondante${matchCount > 1 ? 's' : ''}`
                  : 'Aucun résultat pour cette recherche.'}
              </Text>
            </Grid.Col>
          </Grid>
        ) : null}

        <Grid gutter="md" align="stretch" mb="xs">
          <Grid.Col span={{ base: 12, md: 2 }}>
            <Paper withBorder p="md" radius="md">
              <ScrollArea h={SCROLL_HEIGHT} type="scroll" offsetScrollbars>
                <Stack gap={4} pb="md">
                  <Button
                    variant={!searchQuery ? 'light' : 'subtle'}
                    color={getAccentColor()}
                    size="compact-sm"
                    fullWidth
                    justify="flex-start"
                    onClick={() => setSearchQuery('')}
                  >
                    Toutes
                  </Button>
                  {versions.map((version) => {
                    const selected = activeVersion === version;
                    return (
                      <Button
                        key={version}
                        variant={selected ? 'light' : 'subtle'}
                        color={selected ? getAccentColor() : 'gray'}
                        size="compact-sm"
                        fullWidth
                        justify="flex-start"
                        onClick={() => setSearchQuery(versionSearchQuery(version))}
                      >
                        v{version}
                      </Button>
                    );
                  })}
                </Stack>
              </ScrollArea>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 10 }}>
            <MarkdownDocViewer
              showSearch={false}
              scrollHeight={SCROLL_HEIGHT}
              searchQuery={searchQuery}
              markdown={releaseNotes}
              emptyText="Aucune note de version disponible."
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </JlfcModal>
  );
}
